import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getUser, updateUser, deleteUser, grantAdmin, revokeAdmin,
  grantOperator, revokeOperator, lockChat, unlockChat,
  setBattery, adjustBattery, getBatteryHistory,
  getUserTypeLabel, getUserTypeBadgeColor,
} from '../services/userService';
import { getUserDMs } from '../services/dmService';
import { useAuthStore } from '../stores/authStore';
import { formatTimestamp } from '../utils/time';
import { coordsToFrequency } from '../utils/frequency';
import type { User, BatteryTransaction, DirectMessage } from '../types';

export default function UserDetailPage() {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const { adminUser } = useAuthStore();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [batteryHistory, setBatteryHistoryData] = useState<BatteryTransaction[]>([]);
  const [batteryInput, setBatteryInput] = useState('');
  const [tab, setTab] = useState<'info' | 'battery' | 'dm'>('info');
  const [dms, setDms] = useState<{ inbox: DirectMessage[]; sent: DirectMessage[] }>({ inbox: [], sent: [] });
  const [dmTab, setDmTab] = useState<'inbox' | 'sent'>('inbox');

  useEffect(() => { if (uid) loadUser(); }, [uid]);

  async function loadUser() {
    if (!uid) return;
    setLoading(true);
    try {
      const u = await getUser(uid);
      setUser(u);
    } finally { setLoading(false); }
  }

  async function loadBatteryHistory() {
    if (!uid) return;
    setBatteryHistoryData(await getBatteryHistory(uid));
  }

  async function loadDMs() {
    if (!uid) return;
    setDms(await getUserDMs(uid));
  }

  useEffect(() => {
    if (tab === 'battery') loadBatteryHistory();
    if (tab === 'dm') loadDMs();
  }, [tab]);

  async function handleGiveCells() {
    if (!uid || !batteryInput) return;
    const amount = parseInt(batteryInput);
    if (isNaN(amount) || amount <= 0) return alert('유효한 금액을 입력하세요.');
    await adjustBattery(uid, amount, adminUser?.uid || 'admin');
    setBatteryInput('');
    loadUser();
    if (tab === 'battery') loadBatteryHistory();
  }

  async function handleDeductCells() {
    if (!uid || !batteryInput) return;
    const amount = parseInt(batteryInput);
    if (isNaN(amount) || amount <= 0) return alert('유효한 금액을 입력하세요.');
    await adjustBattery(uid, -amount, adminUser?.uid || 'admin');
    setBatteryInput('');
    loadUser();
    if (tab === 'battery') loadBatteryHistory();
  }

  async function handleSetCells() {
    if (!uid || !batteryInput) return;
    const amount = parseInt(batteryInput);
    if (isNaN(amount) || amount < 0) return alert('유효한 금액을 입력하세요.');
    await setBattery(uid, amount, adminUser?.uid || 'admin');
    setBatteryInput('');
    loadUser();
    if (tab === 'battery') loadBatteryHistory();
  }

  async function handleDelete() {
    if (!uid || !confirm('이 사용자를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    await deleteUser(uid);
    navigate('/users');
  }

  if (loading) return <div className="text-gray-500">로딩 중...</div>;
  if (!user) return <div className="text-red-500">사용자를 찾을 수 없습니다.</div>;

  const freq = user.lat != null && user.lng != null ? coordsToFrequency(user.lat, user.lng) : 'N/A';

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/users')} className="text-gray-500 hover:text-gray-800">&larr; 목록</button>
        <h1 className="text-2xl font-bold">{user.nickname || 'Unknown'}</h1>
        <span className={`px-2 py-0.5 text-xs rounded-full ${getUserTypeBadgeColor(user)}`}>
          {getUserTypeLabel(user)}
        </span>
        {user.chatLocked && <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">채팅 잠금</span>}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {(['info', 'battery', 'dm'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              tab === t ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'info' ? '기본 정보' : t === 'battery' ? '배터리 이력' : '쪽지 이력'}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="font-semibold mb-4">기본 정보</h2>
            <dl className="space-y-2 text-sm">
              {[
                ['UID', user.uid],
                ['이메일', user.email],
                ['닉네임', user.nickname],
                ['성별', user.gender === 'male' ? '남성' : user.gender === 'female' ? '여성' : '미지정'],
                ['배터리', `${(user.battery || 0).toLocaleString()} 셀`],
                ['감도(반경)', `${(user.radius || 0).toLocaleString()}m`],
                ['주파수', freq],
                ['위치', user.lat != null ? `${user.lat?.toFixed(6)}, ${user.lng?.toFixed(6)}` : 'N/A'],
                ['언어', user.language || 'ko'],
                ['가입일', formatTimestamp(user.createdAt)],
                ['최근활동', formatTimestamp(user.lastActiveAt)],
                ['자기소개', user.introduction || '-'],
              ].map(([k, v]) => (
                <div key={k as string} className="flex">
                  <dt className="w-28 text-gray-500 flex-shrink-0">{k}</dt>
                  <dd className="font-medium break-all">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            {/* Battery */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="font-semibold mb-3">배터리 관리</h2>
              <div className="flex gap-2 mb-3">
                <input
                  type="number"
                  value={batteryInput}
                  onChange={e => setBatteryInput(e.target.value)}
                  placeholder="셀 수량"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={handleGiveCells} className="px-3 py-1.5 text-sm bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100">지급</button>
                <button onClick={handleDeductCells} className="px-3 py-1.5 text-sm bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100">차감</button>
                <button onClick={handleSetCells} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100">절대값 설정</button>
              </div>
            </div>

            {/* Permissions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="font-semibold mb-3">권한 관리</h2>
              <div className="flex flex-wrap gap-2">
                {user.isAdmin
                  ? <button onClick={() => { revokeAdmin(uid!); loadUser(); }} className="px-3 py-1.5 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg">관리자 해제</button>
                  : <button onClick={() => { grantAdmin(uid!); loadUser(); }} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg">관리자 부여</button>
                }
                {user.isOperator
                  ? <button onClick={() => { revokeOperator(uid!); loadUser(); }} className="px-3 py-1.5 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg">운영자 해제</button>
                  : <button onClick={() => { grantOperator(uid!); loadUser(); }} className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 border border-purple-200 rounded-lg">운영자 부여</button>
                }
                {user.chatLocked
                  ? <button onClick={() => { unlockChat(uid!); loadUser(); }} className="px-3 py-1.5 text-sm bg-green-50 text-green-700 border border-green-200 rounded-lg">채팅 잠금 해제</button>
                  : <button onClick={() => { lockChat(uid!); loadUser(); }} className="px-3 py-1.5 text-sm bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg">채팅 잠금</button>
                }
              </div>
            </div>

            {/* Profile */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="font-semibold mb-3">프로필</h2>
              {user.profileImageUrl && (
                <img src={user.profileImageUrl} alt="프로필" className="w-20 h-20 rounded-full object-cover mb-3" />
              )}
              <div className="text-sm text-gray-500">
                <p>세션: {user.activeSessionDevice || 'N/A'}</p>
                <p>푸시: {user.pushNotificationsEnabled ? '활성' : '비활성'} | 토큰: {user.pushToken ? '있음' : '없음'}</p>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-red-50 rounded-xl border border-red-200 p-6">
              <h2 className="font-semibold text-red-800 mb-3">위험 영역</h2>
              <button onClick={handleDelete} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">
                사용자 삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'battery' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-semibold">배터리 이력 ({batteryHistory.length}건)</h2>
            <button onClick={loadBatteryHistory} className="text-sm text-blue-600 hover:underline">새로고침</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">유형</th>
                  <th className="px-4 py-2 text-right">금액</th>
                  <th className="px-4 py-2 text-left">설명</th>
                  <th className="px-4 py-2 text-left">시간</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {batteryHistory.map((tx, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{tx.type}</td>
                    <td className={`px-4 py-2 text-right font-mono ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount >= 0 ? '+' : ''}{tx.amount.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-gray-500">{tx.description || '-'}</td>
                    <td className="px-4 py-2 text-gray-400">{new Date(tx.timestamp).toLocaleString('ko-KR')}</td>
                  </tr>
                ))}
                {batteryHistory.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">배터리 이력이 없습니다.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'dm' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex gap-3">
            <button onClick={() => setDmTab('inbox')} className={`text-sm font-medium ${dmTab === 'inbox' ? 'text-blue-600' : 'text-gray-500'}`}>
              받은 쪽지 ({dms.inbox.length})
            </button>
            <button onClick={() => setDmTab('sent')} className={`text-sm font-medium ${dmTab === 'sent' ? 'text-blue-600' : 'text-gray-500'}`}>
              보낸 쪽지 ({dms.sent.length})
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">{dmTab === 'inbox' ? '보낸 사람' : '받는 사람'}</th>
                  <th className="px-4 py-2 text-left">내용</th>
                  <th className="px-4 py-2 text-left">읽음</th>
                  <th className="px-4 py-2 text-left">시간</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(dmTab === 'inbox' ? dms.inbox : dms.sent).map(dm => (
                  <tr key={dm.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{dmTab === 'inbox' ? dm.senderNickname : dm.recipientNickname}</td>
                    <td className="px-4 py-2 truncate max-w-xs">{dm.content}</td>
                    <td className="px-4 py-2">{dm.isRead ? 'O' : '-'}</td>
                    <td className="px-4 py-2 text-gray-400">{formatTimestamp(dm.sentAt, 'short')}</td>
                  </tr>
                ))}
                {(dmTab === 'inbox' ? dms.inbox : dms.sent).length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">쪽지가 없습니다.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
